import { Button } from "@/components/ui/button";
import { HiPencil, HiPlus, HiTrash } from "react-icons/hi2";
import * as z from "zod";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetOutline, useProposalStore } from "@/stores/ProposalStore";

function RowActions({ row, proposal_id, searchType, ...props }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex gap-2" {...props}>
      <div className="relative group">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <HiPlus className="text-gray-600" />
              <span className="sr-only">Add</span>
            </Button>
          </DialogTrigger>
          <AddSectionDialogContent
            setOpen={setOpen}
            row={row}
            searchType={searchType}
            proposal_id={proposal_id}
          />
        </Dialog>
        <span className="absolute bottom-full left-1/2 mb-1 hidden w-max transform -translate-x-1/2 rounded bg-gray-700 px-2 py-1 text-sm text-white opacity-0 group-hover:block group-hover:opacity-100">
          Add a new section
        </span>
      </div>
      <div className="relative group">
        <Dialog>
          <DialogTrigger>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <HiPencil className="text-gray-600" />
              <span className="sr-only">Edit</span>
            </Button>
          </DialogTrigger>
          {/* Pass data dynamically, not as props captured in closure */}
          <EditSectionDialogContent
            getSection={() => row.original}
            proposal_id={proposal_id}
            searchType={searchType}
          />
        </Dialog>
        <span className="absolute bottom-full left-1/2 mb-1 hidden w-max transform -translate-x-1/2 rounded bg-gray-700 px-2 py-1 text-sm text-white opacity-0 group-hover:block group-hover:opacity-100">
          Edit section
        </span>
      </div>
      <div className="relative group">
        <Dialog>
          <DialogTrigger>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <HiTrash className="text-gray-600" />
              <span className="sr-only">Delete</span>
            </Button>
          </DialogTrigger>
          {/* Pass data dynamically, not as props captured in closure */}
          <DeleteSectionDialogContent
            getSection={() => row.original}
            searchType={searchType}
            proposal_id={proposal_id}
          />
        </Dialog>
        <span
          className="absolute bottom-full left-0 mb-1 hidden w-max rounded bg-gray-700 px-2 py-1 text-sm text-white opacity-0 group-hover:block group-hover:opacity-100"
          style={{ transform: "translateX(-70%)" }}
        >
          Delete section
        </span>
      </div>
    </div>
  );
}

function DeleteSectionDialogContent({ getSection, proposal_id, searchType }) {
  const deleteSection = useProposalStore((state) => state.deleteSection);
  const [section, setSection] = useState(null);

  // Get the latest section data when dialog opens
  useEffect(() => {
    setSection(getSection());
  }, [getSection]);

  function onSubmit() {
    if (section) {
      deleteSection(searchType, proposal_id, section.title);
    }
  }

  if (!section) return null;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this section?
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="secondary">Close</Button>
        </DialogClose>
        <DialogClose asChild>
          <Button type="submit" variant="destructive" onClick={onSubmit}>
            Delete
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}

function AddSectionDialogContent({ row, proposal_id, searchType, setOpen }) {
  const outline = useGetOutline(searchType, proposal_id);
  const addSection = useProposalStore((state) => state.addSection);

  const FormSchema = z
    .object({
      section_number: z.coerce
        .number()
        .gte(1)
        .lte(outline.main_sections.length + 1),

      subsection_number: z.coerce.number().gte(0),

      title: z.string().trim().min(1, {
        message: "Title cannot be empty",
      }),

      pages: z.coerce.number().gte(1).lte(10),
      writing_plan: z.string().trim().min(1, {
        message: "AI Writing Plan cannot be empty",
      }),
    })
    .superRefine((data, ctx) => {
      const { section_number, subsection_number } = data;

      if (subsection_number > 0) {
        const section = outline.main_sections[section_number - 1];
        if (!section || subsection_number > section.sub_sections.length + 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid subsection number for the selected section",
            path: ["subsection_number"],
          });
        }
      }
    });

  // Get the latest values when the dialog is opened
  const [mainIdx, subIdx] = row.id.split(".");

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pages: 1,
      section_number: subIdx ? parseInt(mainIdx) + 1 : parseInt(mainIdx) + 2,
      subsection_number: subIdx ? parseInt(subIdx) + 2 : 0,
    },
  });

  function onSubmit(data) {
    const newSection = { ...data, sub_sections: [] };

    addSection(
      searchType,
      proposal_id,
      newSection,
      data.section_number,
      data.subsection_number
    ); // Update Zustand store
    setOpen(false);
  }

  return (
    <DialogContent className="max-w-6xl">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
            <DialogDescription>
              Add a new section/subsection to the outline
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="mb-4">
              <FormLabel className="text-base">Section Number</FormLabel>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <FormField
                control={form.control}
                name="section_number"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <FormItem className="w-10">
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </FormItem>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              .
              <FormField
                control={form.control}
                name="subsection_number"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <FormItem className="w-10">
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </FormItem>
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base">Title</FormLabel>
                    </div>
                    <FormControl>
                      <Input placeholder="Enter section title..." {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pages"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base"># Pages</FormLabel>
                      <FormDescription>
                        The length of this section in pages
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="writing_plan"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base">
                        AI Writing Plan
                      </FormLabel>
                      <FormDescription>
                        Describe to the AI how you want this section to be
                        written.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Textarea
                        placeholder="Enter AI writing plan..."
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <DialogFooter>
            <Button type="submit">Add section</Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

function EditSectionDialogContent({ getSection, searchType, proposal_id }) {
  const updateSection = useProposalStore((state) => state.updateSection);
  const [section, setSection] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Initialize form values when dialog opens
  const FormSchema = z.object({
    title: z.string().trim().min(1, {
      message: "Title cannot be empty",
    }),
    pages: z.coerce.number().gte(1).lte(10),
    writing_plan: z.string().trim().min(1, {
      message: "AI Writing Plan cannot be empty",
    }),
  });

  const form = useForm({
    resolver: zodResolver(FormSchema),
  });

  // Update form values when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      const currentSection = getSection();
      setSection(currentSection);

      // Reset form with the latest values
      form.reset({
        title: currentSection.title,
        pages: currentSection.pages,
        writing_plan: currentSection.writing_plan,
      });
    }
  }, [isDialogOpen, getSection, form]);

  function onSubmit(data) {
    if (section) {
      updateSection(searchType, proposal_id, section.title, data);
    }
  }

  if (!section)
    return (
      <DialogContent
        className="max-w-6xl"
        onOpenAutoFocus={() => setIsDialogOpen(true)}
        onCloseAutoFocus={() => setIsDialogOpen(false)}
      >
        Loading...
      </DialogContent>
    );

  return (
    <DialogContent
      className="max-w-6xl"
      onOpenAutoFocus={() => setIsDialogOpen(true)}
      onCloseAutoFocus={() => setIsDialogOpen(false)}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <DialogHeader>
            <DialogTitle>Modify Section</DialogTitle>
            <DialogDescription>Modify the section properties</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base">Title</FormLabel>
                    </div>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pages"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base"># Pages</FormLabel>
                      <FormDescription>
                        The length of this section in pages
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="writing_plan"
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base">
                        AI Writing Plan
                      </FormLabel>
                      <FormDescription>
                        Describe to the AI how you want this section to be
                        written.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Textarea className="h-32" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="submit">Save changes</Button>
            </DialogClose>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

function RowComponent({ row, children, proposal_id, searchType }) {
  return (
    <Dialog>
      <DialogTrigger className="text-left">{children}</DialogTrigger>
      <EditSectionDialogContent
        getSection={() => row.original}
        searchType={searchType}
        proposal_id={proposal_id}
      />
    </Dialog>
  );
}

const columnHelper = createColumnHelper();
export const getOutlineColumns = ({ proposal_id, searchType }) => {
  return [
    columnHelper.display({
      id: "id",
      header: "#",
      cell: ({ row }) => (
        <div
          className="text-muted-foreground"
          style={{
            paddingLeft: `${row.depth}rem`,
          }}
        >
          <span className={`${row.depth === 0 && "font-medium"}`}>
            {row.id.includes(".")
              ? (parseFloat(row.id) + 1.1).toFixed(1)
              : parseInt(row.id) + 1}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("title", {
      id: "title",
      header: "Title",
      cell: ({ row, getValue }) =>
        getValue() && (
          <RowComponent
            row={row}
            proposal_id={proposal_id}
            searchType={searchType}
          >
            <div
              style={{
                paddingLeft: `${row.depth * 2}rem`,
              }}
            >
              <span className={`${row.depth === 0 && "font-medium"}`}>
                {getValue()}
              </span>
            </div>
          </RowComponent>
        ),
    }),

    columnHelper.accessor("pages", {
      id: "pages",
      header: "Pages",
      cell: ({ row, getValue }) => (
        <RowComponent
          row={row}
          proposal_id={proposal_id}
          searchType={searchType}
        >
          {getValue()}
        </RowComponent>
      ),
    }),
    columnHelper.accessor("writing_plan", {
      id: "writing_plan",
      header: "Writing Plan",
      cell: ({ row, getValue }) => (
        <RowComponent
          row={row}
          proposal_id={proposal_id}
          searchType={searchType}
        >
          {getValue()}
        </RowComponent>
      ),
    }),
    {
      id: "actions",
      header: () => <div id="generate-table-actions-header">Actions</div>,
      cell: ({ row }) => (
        <RowActions
          row={row}
          proposal_id={proposal_id}
          searchType={searchType}
        />
      ),
    },
  ];
};
